import { auditLogger, containsMaliciousPatterns, isValidUUID, sanitizeInput } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

import { withSecurity } from './secureServiceWrapper';

export interface LawFirm {
  id: string;
  lawfirmname: string;
  created_at: string;
}

export const lawFirmService = {
  findOrCreateLawFirm: async (firmName: string): Promise<string | null> => {
    return withSecurity(
      async () => {
        const sanitizedName = sanitizeInput(firmName.trim());

        if (!sanitizedName || sanitizedName.length === 0) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'empty_firm_name', service: 'lawFirm' });
          throw new Error('Firm name is required');
        }

        if (sanitizedName.length > 255) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'firm_name_too_long', service: 'lawFirm' });
          throw new Error('Firm name is too long');
        }

        if (containsMaliciousPatterns(sanitizedName)) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'lawFirm', action: 'findOrCreate' });
          throw new Error('Invalid input detected');
        }

        const supabase = await getSupabase();

        const { data: existingFirm } = await supabase
          .from('law_firms')
          .select('id')
          .eq('lawfirmname', sanitizedName)
          .maybeSingle();

        if (existingFirm) {
          return existingFirm.id;
        }

        const { data: newFirm, error: createError } = await supabase
          .from('law_firms')
          .insert([{ lawfirmname: sanitizedName }])
          .select('id')
          .single();

        if (createError) throw createError;
        return newFirm.id;
      },
      { requireAuth: true, rateLimitKey: 'lawfirm_create', rateLimitMax: 20 }
    );
  },

  getLawFirm: async (firmId: string): Promise<LawFirm | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(firmId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_firm_id', service: 'lawFirm' });
          return null;
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase.from('law_firms').select('*').eq('id', firmId).maybeSingle();

        if (error) throw error;
        return data;
      },
      { requireAuth: true, rateLimitKey: 'lawfirm_get' }
    );
  },

  searchLawFirms: async (searchTerm: string): Promise<LawFirm[]> => {
    return withSecurity(
      async () => {
        const sanitizedTerm = sanitizeInput(searchTerm.trim());

        if (!sanitizedTerm || sanitizedTerm.length === 0) {
          return [];
        }

        if (sanitizedTerm.length > 100) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'search_term_too_long', service: 'lawFirm' });
          throw new Error('Search term is too long');
        }

        if (containsMaliciousPatterns(sanitizedTerm)) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'lawFirm', action: 'search' });
          throw new Error('Invalid input detected');
        }

        const escapedTerm = sanitizedTerm.replace(/[%_]/g, '\\$&').replace(/'/g, "''");

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('law_firms')
          .select('*')
          .ilike('lawfirmname', `%${escapedTerm}%`)
          .limit(10);

        if (error) throw error;
        return data || [];
      },
      { requireAuth: true, rateLimitKey: 'lawfirm_search', rateLimitMax: 50 }
    );
  },
};
